import { useState } from 'react';
import PhoneInputWithCountry from 'react-phone-number-input/input';
import styled from '@emotion/styled';
import { useIntl } from 'react-intl';

import { brandNeutralColors } from '@anyroad/theme';
import { COUNTRIES } from './Helpers';

const inputHeight = '48px';
const countrySelectWidth = '80px';

const PhoneInputs = styled.div`
  display: flex;
  height: ${inputHeight};
`;

const StyledPhoneInput = styled(PhoneInputWithCountry, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'errorColor',
})<{
  hasError?: boolean;
  errorColor?: string;
}>`
  width: calc(100% - ${countrySelectWidth}) !important;
  height: 100% !important;
  border: 1px solid
    ${(props) =>
      props.hasError ? props.errorColor : brandNeutralColors.borderAction};
  color: ${(props) =>
    props.hasError ? props.errorColor : brandNeutralColors.text.primary};
  border-left: 0 !important;
  border-radius: 0 !important;
  padding: 10px 15px !important;

  &::placeholder {
    color: ${brandNeutralColors.text.placeholder};
  }

  &:focus {
    outline: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: ${countrySelectWidth};
`;

const Select = styled.select`
  appearance: none;
  position: absolute;
  opacity: 0;
  width: 100% !important;
  height: 100% !important;
  border: 1px solid ${brandNeutralColors.borderAction};
  &:hover {
    cursor: pointer;
  }
`;

const Button = styled.button<{
  hovered?: boolean;
  hasError?: boolean;
  errorColor?: string;
}>`
  font-size: 16px;
  display: flex;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid
    ${(props) =>
      props.hasError ? props.errorColor : brandNeutralColors.borderAction};
  background-color: ${(props) =>
    props.hovered ? 'rgb(243, 243, 243)' : 'white'};
  padding: 10px;
`;

const ArrowDown = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid ${brandNeutralColors.text.icon};
  margin-right: 6px;
`;

// Types
interface PhoneInputProps {
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  countryCode?: string;
  hasError?: boolean;
  dataTestId?: string;
  ariaLabelledBy?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const PhoneInput = ({
  phoneNumber,
  setPhoneNumber,
  countryCode: cc = 'US',
  hasError = false,
  dataTestId,
  ariaLabelledBy,
  onFocus,
  onBlur,
}: PhoneInputProps) => {
  const { formatMessage } = useIntl();
  const [hovered, setHovered] = useState(false);
  const [countryCode, setCountryCode] = useState(cc);
  const errorColor = '#ed1c24';

  function onCountryChange(value: string) {
    setCountryCode(value);
    // Swap calling codes if there is one
    const currentCountry = COUNTRIES.find((c) => c.code === countryCode);
    const newCountry = COUNTRIES.find((c) => c.code === value);

    if (currentCountry && newCountry) {
      const callingCode = currentCountry.dialCode;
      if (phoneNumber && phoneNumber.indexOf(callingCode) === 0) {
        const newCallingCode = newCountry.dialCode;
        setPhoneNumber(`${newCallingCode}${phoneNumber.split(callingCode)[1]}`);
      }
    }
  }

  const getCountryCallingCode = (code: string): string => {
    const country = COUNTRIES.find((c) => c.code === code);
    return country ? country.dialCode.replace('+', '') : '1';
  };

  const getCountryName = (code: string): string => {
    const country = COUNTRIES.find((c) => c.code === code);
    return country ? country.name : code;
  };

  return (
    <PhoneInputs>
      <SelectContainer>
        <Button
          data-testid="phone-input-button"
          className="phone-input-button"
          hovered={hovered}
          hasError={hasError}
          errorColor={errorColor}
        >
          <ArrowDown />+{getCountryCallingCode(countryCode)}
        </Button>

        <Select
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          value={countryCode}
          onChange={(e) => onCountryChange(e.target.value)}
          data-testid={dataTestId}
          aria-label="country code"
        >
          {COUNTRIES.map((country) => {
            return (
              <option key={country.code} value={country.code}>
                {getCountryName(country.code)}
                &nbsp; {country.dialCode}
              </option>
            );
          })}
        </Select>
      </SelectContainer>
      <StyledPhoneInput
        placeholder={formatMessage({
          defaultMessage: 'Phone Number',
          description: 'Phone number placeholder',
        })}
        country={countryCode as any}
        value={phoneNumber}
        onChange={(val) => (val ? setPhoneNumber(val) : setPhoneNumber(''))}
        onFocus={onFocus}
        onBlur={onBlur}
        hasError={hasError}
        errorColor={errorColor}
        aria-labelledby={ariaLabelledBy}
      />
    </PhoneInputs>
  );
};
